import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { useCallback } from 'react'

import { useActiveJourneyId } from '#journeys/activeJourneyStore'

import { questsInJourney } from './questJourney'
import {
  getQuestRepository,
  type RecordOverridesMutation,
} from './questRepository'
import type { Quest, QuestDoc } from './schemas'
import stripQuestReferencesToSubmission from './stripQuestReferencesToSubmission'

export { useListQuestTemplatesQuery } from './templates'

export const questKeys = {
  all: ['quests'] as const,
  lists: () => [...questKeys.all, 'list'] as const,
  shows: () => [...questKeys.all, 'show'] as const,
  show: (id: string | undefined) => [...questKeys.shows(), id] as const,
}

/**
 * The quests of the journey in hand, which is every quest any caller wants.
 *
 * Filtered here rather than by each reader: the list, the projection and the
 * tuning sheet all have to agree about which quests count, and a reader that
 * forgot would quietly put another journey's quests into the chart.
 *
 * Selected rather than queried: one fetch holds every journey's quests, so
 * switching journeys costs a filter and not a read.
 */
export const useListQuestsQuery = (
  options: { enabled?: boolean } = {}
): UseQueryResult<QuestDoc[], Error> => {
  const journeyId = useActiveJourneyId()
  const select = useCallback(
    (quests: QuestDoc[]) => questsInJourney(quests, journeyId),
    [journeyId]
  )

  return useQuery({
    queryKey: questKeys.lists(),
    queryFn: () => getQuestRepository().list(),
    select,
    retry: false,
    enabled: options.enabled ?? true,
  })
}

export const useShowQuestQuery = (
  id: string | undefined
): UseQueryResult<QuestDoc | undefined, Error> =>
  useQuery({
    queryKey: questKeys.show(id),
    queryFn: () =>
      id ? getQuestRepository().show(id) : Promise.resolve(undefined),
    enabled: !!id,
    retry: false,
  })

export const useCreateQuestMutation = (): UseMutationResult<
  string,
  Error,
  Quest
> => {
  const qc = useQueryClient()
  const journeyId = useActiveJourneyId()

  return useMutation({
    // Stamped here rather than at each of the three call sites: a quest is
    // made in whichever journey is open, and a caller that forgot to say so
    // would land it in the base one for good.
    mutationFn: (entity: Quest) =>
      getQuestRepository().create({ journeyId, ...entity }),
    onSuccess: () => qc.invalidateQueries({ queryKey: questKeys.all }),
  })
}

export const useUpdateQuestMutation = (): UseMutationResult<
  void,
  Error,
  [string, Quest]
> => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ([id, entity]) => getQuestRepository().update(id, entity),
    onSuccess: () => qc.invalidateQueries({ queryKey: questKeys.all }),
  })
}

/**
 * The write path for everything one quest does to another submission's records.
 *
 * Takes a function, not a document. Two rapid edits therefore compose instead of
 * racing — see `QuestRepository.updateRecordOverrides` for the failure this
 * replaces (ZL-1113).
 *
 * The invalidation is conditional: a write that changed nothing must not cost a
 * refetch and a scenario rebuild.
 */
export const useUpdateQuestRecordOverridesMutation = (): UseMutationResult<
  boolean,
  Error,
  [string, RecordOverridesMutation]
> => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ([id, mutate]) =>
      getQuestRepository().updateRecordOverrides(id, mutate),
    onSuccess: changed => {
      if (changed) qc.invalidateQueries({ queryKey: questKeys.all })
    },
  })
}

export const useSetQuestEnabledMutation = (): UseMutationResult<
  void,
  Error,
  { id: string; enabled: boolean }
> => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, enabled }) =>
      getQuestRepository().setEnabled(id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: questKeys.all }),
  })
}

export const useRenameQuestMutation = (): UseMutationResult<
  void,
  Error,
  { id: string; name: string }
> => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name }) => getQuestRepository().rename(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: questKeys.all }),
  })
}

export const useDeleteQuestMutation = (): UseMutationResult<
  void,
  Error,
  string
> => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await getQuestRepository().delete(id)
      // Strip any tunings in OTHER quests that were targeting this quest's
      // answers — quests can be tuned by other quests.
      await stripQuestReferencesToSubmission(id)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: questKeys.all }),
  })
}
